<template>
  <div>
    <b-breadcrumb>
      <b-breadcrumb-item to="/home" @click="updateListCategory('listCategoryHome')">Home</b-breadcrumb-item>
      <b-breadcrumb-item href="#accountDetails" active>Account Details</b-breadcrumb-item>
    </b-breadcrumb>
    <h1 class="template-name">Account Details</h1>
    <p>Review your personal information.</p>
    <hr>
    <h2 class="section-title">Profile Picture</h2>
    <div class="profile-picture-section">
      <b-row>
        <b-col>
          <b-img v-if="userDetails.profilePictureUrl" class="profile-picture" :src="userDetails.profilePictureUrl"
                 rounded="circle"
                 alt="User profile picture"></b-img>

          <b-img v-else class="profile-picture" :src="require('../../assets/default_profile.png')" rounded="circle"
                 alt="User profile picture"></b-img>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button class="secondary-input-button" v-b-modal.account-details-profile-picture-modal>Update profile
            picture
          </b-button>
        </b-col>
      </b-row>
    </div>
    <hr>
    <h2 class="section-title">Account Information</h2>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">Full name</label>
          <b-form-input
              type="text"
              :state="informationValidation.isFullNameProvided"
              v-model="userDetails.fullName"></b-form-input>

          <b-form-invalid-feedback v-if="!informationValidation.isFullNameProvided">
            This field must not be empty.
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
    </div>
    <div class="input-container">
      <b-row>
        <b-col>
          <label class="input-title">Email</label>
          <b-form-input
              type="email"
              :state="informationValidation.isEmailProvided && informationValidation.isEmailValid"
              v-model="userDetails.email" disabled></b-form-input>

          <b-form-invalid-feedback v-if="!informationValidation.isEmailProvided">
            This field must not be empty.
          </b-form-invalid-feedback>

          <b-form-invalid-feedback v-if="!informationValidation.isEmailValid">
            Please provide a valid email address.
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
    </div>
    <b-button class="secondary-input-button" block @click="updateDetails">Update details</b-button>
    <hr>
    <b-button variant="outline-danger" @click="logOut">Log out</b-button>

    <!-- Sign up profile picture modals -->
    <b-modal id="account-details-profile-picture-modal"
             title="Update Profile Picture"
             hide-footer
             no-close-on-backdrop>
      <input type="file" accept="image/*" @change="onFileChange" id="profile-picture-input"/>

      <div id="preview">
        <img v-if="userDetails.profilePictureUrl" :src="userDetails.profilePictureUrl"/>
      </div>

      <p v-if="userDetails.profilePictureUrl" class="current-profile-picture-text">This is your current profile
        picture</p>
      <b-button variant="outline-danger" :disabled="!userDetails.profilePictureUrl" block @click="removeProfilePicture">
        Remove profile picture
      </b-button>
    </b-modal>
  </div>
</template>

<script>
  import {email, required} from "vuelidate/lib/validators"
  import {mapActions, mapGetters} from "vuex"
  import {auth, db, storage} from "../../main"

  export default {
    name: "AccountDetails",
    data() {
      return {
        userDetails: {
          id: null,
          fullName: null,
          email: null,
          profilePictureUrl: null
        },
        informationValidation: {
          isFullNameProvided: null,
          isEmailProvided: null,
          isEmailValid: null
        },
        profilePictureFile: null
      }
    },
    validations: {
      userDetails: {
        fullName: {
          required
        },
        email: {
          required,
          email
        }
      }
    },
    methods: {
      ...mapActions([
        "updateListCategory"
      ]),
      onFileChange(e) {
        this.profilePictureFile = e.target.files[0];

        if (this.profilePictureFile !== undefined) {
          this.userDetails.profilePictureUrl = URL.createObjectURL(this.profilePictureFile);
          this.uploadProfilePicture()
        } else {
          this.userDetails.profilePictureUrl = null
        }
      },
      uploadProfilePicture() {
        let user = auth.currentUser

        // create a storage ref
        let storageRef = storage.ref('profileImages').child(user.uid + '.jpg')
        // upload file
        storageRef.put(this.profilePictureFile)
          .then(snapshot => {
            // update the database
            snapshot.ref.getDownloadURL().then(downloadUrl => {
              db.collection('users')
                .doc(user.uid)
                .update({
                  profilePictureUrl: downloadUrl
                })

              // change the actual user profile picture
              user.updateProfile({
                photoURL: downloadUrl
              })
                .then(() => {
                  // update the local user picture
                  let updatedUserData = {
                    id: user.uid,
                    displayName: user.displayName,
                    email: user.email,
                    photoURL: user.photoURL
                  }

                  this.$store.commit('setUser', updatedUserData)
                })

              // update the profile picture in the user_collaborative_to_do_list_participation
              db
                .collectionGroup('user_collaborative_to_do_list_participation')
                .where('collaboratorId', '==', this.$store.getters.user.data.id)
                .get()
                .then(querySnapshot => {
                  querySnapshot.docs.map(doc => {
                    db
                      .collection('users/' + this.$store.getters.user.data.id + '/user_collaborative_to_do_list_participation')
                      .doc(doc.id)
                      .update('collaboratorProfilePictureUrl', downloadUrl)
                  })
                })

              // update the profile picture in the comments
              db
                .collectionGroup('comments')
                .where('collaboratorId', '==', this.$store.getters.user.data.id)
                .get()
                .then(querySnapshot => {
                  querySnapshot.docs.map(doc => {
                    db
                      .collection('collaborative_to_do_lists/' + doc.data().toDoListId + '/tasks/' + doc.data().taskId + '/comments')
                      .doc(doc.id)
                      .update('collaboratorProfilePictureUrl', downloadUrl)
                  })
                })
            })
          })
      },
      removeProfilePicture() {
        let user = auth.currentUser

        // remove the profile picture
        let storageRef = storage.refFromURL(user.photoURL)
        storageRef.delete()

        user.updateProfile({
          photoURL: null
        })

        db.collection('users')
          .doc(this.userDetails.id)
          .update({
            profilePictureUrl: null
          })

        // update the profile picture in the user_collaborative_to_do_list_participation
        db
          .collectionGroup('user_collaborative_to_do_list_participation')
          .where('collaboratorId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('users/' + this.$store.getters.user.data.id + '/user_collaborative_to_do_list_participation')
                .doc(doc.id)
                .update('collaboratorProfilePictureUrl', null)
            })
          })

        // update the profile picture in the comments
        db
          .collectionGroup('comments')
          .where('collaboratorId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('collaborative_to_do_lists/' + doc.data().toDoListId + '/tasks/' + doc.data().taskId + '/comments')
                .doc(doc.id)
                .update('collaboratorProfilePictureUrl', null)
            })
          })

        // update the profile picture in the main sidebar
        let updatedUserData = {
          id: user.uid,
          displayName: user.displayName,
          email: user.email,
          photoURL: null
        }

        this.$store.commit('setUser', updatedUserData)

        this.profilePictureFile = null
        this.userDetails.profilePictureUrl = null
        document.getElementById("profile-picture-input").value = "";

        this.hideModal('account-details-profile-picture-modal')
      },
      updateDetails() {
        // check if all information is valid
        let isValid = true

        if (!this.$v.userDetails.fullName.required) {
          this.informationValidation.isFullNameProvided = false
          isValid = false
        } else {
          this.informationValidation.isFullNameProvided = true
        }

        if (!this.$v.userDetails.email.required) {
          this.informationValidation.isEmailProvided = false
          isValid = false
        } else {
          this.informationValidation.isEmailProvided = true
        }

        if (!this.$v.userDetails.email.email) {
          this.informationValidation.isEmailValid = false
          isValid = false
        } else {
          this.informationValidation.isEmailValid = true
        }

        if (isValid) {
          let user = auth.currentUser

          // change the actual user full name
          user.updateProfile({
            displayName: this.userDetails.fullName
          })
            .then(() => {
              // update the local user full name
              let updatedUserData = {
                id: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL
              }

              this.$store.commit('setUser', updatedUserData)
            })
        }

        // update the user full name in the 'users' directory
        db
          .collection('users')
          .doc(this.$store.getters.user.data.id)
          .update('name', this.userDetails.fullName)

        // update the user full name in the 'tasks' category
        db
          .collectionGroup('tasks')
          .where('assigneeId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              if (doc.data().inboxTask) {
                db
                  .collection('inbox_to_do_lists/' + doc.data().toDoListId + '/tasks')
                  .doc(doc.id)
                  .update('assigneeName', this.userDetails.fullName)
              } else {
                if (doc.data().taskType === 'Personal Task') {
                  db
                    .collection('personal_to_do_lists/' + doc.data().toDoListId + '/tasks')
                    .doc(doc.id)
                    .update('assigneeName', this.userDetails.fullName)
                } else {
                  db
                    .collection('collaborative_to_do_lists/' + doc.data().toDoListId + '/tasks')
                    .doc(doc.id)
                    .update('assigneeName', this.userDetails.fullName)
                }
              }
            })
          })

        // update the user full name as owner in the 'inbox_to_do_lists' directory
        db
          .collection('inbox_to_do_lists')
          .where('ownerId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('inbox_to_do_lists')
                .doc(doc.id)
                .update('ownerName', this.userDetails.fullName)
            })
          })

        // update the user full name as owner in the 'personal_to_do_lists' directory
        db
          .collection('personal_to_do_lists')
          .where('ownerId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('personal_to_do_lists')
                .doc(doc.id)
                .update('ownerName', this.userDetails.fullName)
            })
          })

        // update the user full name as owner in the 'collaborative_to_do_lists' directory
        db
          .collection('collaborative_to_do_lists')
          .where('ownerId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('collaborative_to_do_lists')
                .doc(doc.id)
                .update('ownerName', this.userDetails.fullName)
            })
          })

        // update the user full name in the 'comments' directory
        db
          .collectionGroup('comments')
          .where('collaboratorId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('collaborative_to_do_lists/' + doc.data().toDoListId + '/tasks/' + doc.data().taskId + '/comments')
                .doc(doc.id)
                .update('collaboratorName', this.userDetails.fullName)
            })
          })

        // update the user full name in the 'user_collaborative_to_do_list_participation' directory
        db
          .collectionGroup('user_collaborative_to_do_list_participation')
          .where('collaboratorId', '==', this.$store.getters.user.data.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.docs.map(doc => {
              db
                .collection('users/' + this.$store.getters.user.data.id + '/user_collaborative_to_do_list_participation')
                .doc(doc.id)
                .update('collaboratorName', this.userDetails.fullName)
            })
          })
      },
      hideModal(id) {
        this.$root.$emit('bv::hide::modal', id)
      },
      logOut() {
        this.$store.dispatch('logOut')
        this.$store.dispatch('updateListCategory', '')
      },
      getUserInformation() {
        let user = auth.currentUser

        this.userDetails.id = user.uid
        this.userDetails.fullName = user.displayName
        this.userDetails.email = user.email
        this.userDetails.profilePictureUrl = user.photoURL
      }
    },
    mounted() {
      this.getUserInformation()
    }
  }
</script>

<style scoped>
  #profile-picture-input {
    margin-bottom: 1em;
  }

  .profile-picture-section {
    text-align: center;
  }

  .profile-picture {
    width: 12em;
    height: 12em;
    margin-bottom: 1em;
  }

  #preview {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  #preview img {
    max-width: 100%;
    max-height: 500px;
  }

  .current-profile-picture-text {
    text-align: center;
    margin-top: 1em;
  }

  .input-container {
    margin: 1em 0;
  }

  .input-title {
    font-weight: bold;
  }

  .section-title {
    font-size: 1.5em;
    margin-bottom: 0.5em;
  }

  .upload-button {
    margin-top: 1em;
  }

  .secondary-input-button {
    background-color: white;
    color: #212441;
    padding: 0.5em;
  }

  .secondary-input-button:hover {
    background-color: #212441;
    color: white;
    padding: 0.5em;
  }
</style>
